import React, { useState, useMemo } from 'react';
import {
  Card,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  TextField as MuiTextField,
  IconButton,
} from '@mui/material';
import {
  NotificationImportant as AlertIcon,
  Add as AddIcon,
  Circle as StatusIcon,
  LocationOn as ZoneIcon,
  Delete as DeleteIcon,
  Visibility as ShowIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import HierarchicalFilter, {
  HierarchicalFilterSelections,
} from '../../components/HierarchicalFilter';
import CustomCreateButton from '@/components/CustomCreateButton';
import PaginationControls from '@/components/PaginationControls';
import DeleteWithConfirmationDialog from '@/components/DeleteWithConfirmationDialog';
import TableComponent, { Column } from '@/components/TableComponent';
import RoleIconField from '@/components/RoleIconField';
import { 
  getGetApiAlertsQueryKey, 
  getApiAlerts,
  getGetApiZonesQueryKey,
  getApiZones,
  deleteApiAlertsId  // ← AÑADE ESTE IMPORT para la eliminación
} from '@/api/gen/api';

// DebouncedSearchInput
const DebouncedSearchInput = React.memo(({ 
  onSearchChange, 
  initialValue = '' 
}: { 
  onSearchChange: (value: string) => void;
  initialValue?: string;
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const debounceRef = React.useRef<NodeJS.Timeout>();

  const handleChange = (value: string) => {
    setInputValue(value);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      onSearchChange(value);
    }, 500);
  };

  React.useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  return (
    <MuiTextField
      size="small"
      placeholder="Buscar alertas..."
      value={inputValue}
      onChange={(e) => handleChange(e.target.value)}
      sx={{ minWidth: 200, mb: 0.5 }}
    />
  );
});

const pageSizeOptions = [5, 10, 20, 50];

const buttonStyle = {
  minWidth: '24px',
  width: '24px',
  height: '24px',
  padding: '2px',
  '& .MuiButton-startIcon': { margin: 0 },
  '& svg': { fontSize: '16px' }
};

const AlertListActions = () => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
    <CustomCreateButton
      variant='contained'
      label='Crear Alerta'
      icon={<AddIcon />}
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': { backgroundColor: 'primary.dark' },
      }}
    />
  </Box>
);

// Componente StatusField
const StatusField = ({ record }: { record?: any }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'error';
      case 'STANDBY':
        return 'warning';
      case 'ACKNOWLEDGED':
        return 'info';
      case 'INACTIVE':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Activa';
      case 'STANDBY':
        return 'En espera';
      case 'ACKNOWLEDGED':
        return 'Reconocida';
      case 'INACTIVE':
        return 'Inactiva';
      default:
        return status;
    }
  };

  return (
    <Box display='flex' alignItems='center' gap={1}>
      <StatusIcon
        sx={{
          fontSize: 12,
          color:
            getStatusColor(record?.status) === 'error'
              ? 'error.main'
              : getStatusColor(record?.status) === 'warning'
                ? 'warning.main'
                : getStatusColor(record?.status) === 'info'
                  ? 'info.main'
                  : 'grey.500',
        }}
      />
      <Chip
        label={getStatusLabel(record?.status || 'INACTIVE')}
        color={getStatusColor(record?.status)}
        size='small'
      />
    </Box>
  );
};

// Componente PriorityField
const PriorityField = ({ record }: { record?: any }) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return 'error';
      case 'HIGH':
        return 'warning';
      case 'MEDIUM':
        return 'info';
      case 'LOW':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
        return 'Crítica';
      case 'HIGH':
        return 'Alta';
      case 'MEDIUM':
        return 'Media';
      case 'LOW':
        return 'Baja';
      default:
        return priority;
    }
  };

  return (
    <Chip
      label={getPriorityLabel(record?.priority || 'LOW')}
      color={getPriorityColor(record?.priority)}
      size='small'
      variant='outlined'
    />
  );
};

export const AlertsList: React.FC = () => {
  // Estados para filtros y paginación
  const [filterValues, setFilterValues] = useState<HierarchicalFilterSelections>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({ page: 1, perPage: 10 });
  
  // Estado para el diálogo de eliminación
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    record: null as any,
  });

  const queryClient = useQueryClient();

  // Mutation para eliminar alerta
  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteApiAlertsId(id),
    onSuccess: () => {
      // Invalidar la query para refrescar los datos
      queryClient.invalidateQueries({ queryKey: getGetApiAlertsQueryKey({}) });
      handleCloseDeleteDialog();
    },
    onError: (error) => {
      console.error('Error eliminando alerta:', error);
      // Aquí podrías mostrar un snackbar o alerta de error
    }
  });

  // Construir parámetros para la query de alertas
  const queryParams = useMemo(() => {
    const params: any = {
      page: pagination.page,
      pageSize: pagination.perPage
    };

    if (searchQuery.trim()) params.SearchString = searchQuery;
    if (filterValues.plantId) params.PlantId = filterValues.plantId;
    if (filterValues.zoneId) params.ZoneId = filterValues.zoneId;

    return params;
  }, [filterValues, pagination, searchQuery]);

  // Query para alertas
  const queryKey = getGetApiAlertsQueryKey(queryParams);

  const {
    data: alertsResponse,
    isLoading: isLoadingAlerts,
    error: alertsError,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: () => getApiAlerts(queryParams),
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Query para zonas
  const zonesQueryParams = useMemo(() => ({
    isPaginated: false,
    page: 1,
    pageSize: 1000
  }), []);

  const { data: zonesResponse, isLoading: isLoadingZones } = useQuery({
    queryKey: getGetApiZonesQueryKey(zonesQueryParams),
    queryFn: () => getApiZones(zonesQueryParams),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  // Mapa de zonas para búsqueda rápida
  const zonesMap = useMemo(() => {
    if (!zonesResponse || !Array.isArray(zonesResponse)) return new Map();
    
    const map = new Map();
    zonesResponse.forEach((zone: any) => {
      if (zone && zone.id) {
        const zoneName = zone.zoneName || zone.name || `Zona ${zone.id}`;
        map.set(zone.id, zoneName);
      }
    });
    
    return map;
  }, [zonesResponse]);

  // Extraer datos de alertas con diferentes estructuras posibles
  const { alerts, totalCount, showPagination } = useMemo(() => {
    if (!alertsResponse) return { alerts: [], totalCount: 0, showPagination: false };

    let items = [];
    let total = 0;

    if (Array.isArray(alertsResponse)) {
      items = alertsResponse;
      total = alertsResponse.length;
    } else if (alertsResponse.items && Array.isArray(alertsResponse.items)) {
      items = alertsResponse.items;
      total = alertsResponse.totalCount || alertsResponse.items.length;
    } else if (alertsResponse.data && Array.isArray(alertsResponse.data)) {
      items = alertsResponse.data;
      total = alertsResponse.totalCount || alertsResponse.data.length;
    } else {
      items = [alertsResponse];
      total = 1;
    }
    
    return {
      alerts: items,
      totalCount: total,
      showPagination: total > pagination.perPage
    };
  }, [alertsResponse, pagination.perPage]);

  // Handlers para eliminación
  const handleOpenDeleteDialog = (record: any) => {
    setDeleteDialog({
      open: true,
      record,
    });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      record: null,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteDialog.record) {
      deleteMutation.mutate(deleteDialog.record.id);
    }
  };

  // Handlers
  const handleFilterChange = React.useCallback((selections: HierarchicalFilterSelections) => {
    setFilterValues(selections);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handleSearchChange = React.useCallback((value: string) => {
    setSearchQuery(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePerPageChange = (perPage: number) => {
    setPagination({ perPage, page: 1 });
  };

  // Columnas para la tabla
  const alertColumns: Column[] = [
    {
      key: 'id',
      label: 'ID',
      width: '50px',
    },
    {
      key: 'name',
      label: 'Nombre de la Alerta',
      maxWidth: '150px',
    },
    {
      key: 'status',
      label: 'Estado',
      maxWidth: '120px',
      render: (value, record) => <StatusField record={record} />,
    },
    {
      key: 'priority',
      label: 'Prioridad',
      maxWidth: '100px',
      render: (value, record) => <PriorityField record={record} />,
    },
    {
      key: 'isActive',
      label: 'Activo',
      maxWidth: '80px',
      render: (value) => (
        <Chip
          label={value ? 'Sí' : 'No'}
          color={value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      key: 'zoneId',
      label: 'Zona',
      maxWidth: '100px',
      render: (value, record) => {
        const zoneId = record.zoneId || record.zone?.id;
        const zoneName = zonesMap.get(zoneId);
        
        if (isLoadingZones) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircularProgress size={12} />
              <span>Cargando...</span>
            </Box>
          );
        }
        
        if (!zoneId) {
          return 'Sin zona';
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ZoneIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
            <Link 
              to={`/zones/${zoneId}/show`}
              style={{ 
                textDecoration: 'none',
                color: 'inherit',
                cursor: 'pointer'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  '&:hover': { 
                    color: 'primary.main',
                    textDecoration: 'underline' 
                  } 
                }}
              >
                {zoneName || `Zona ${zoneId}`}
              </Typography>
            </Link>
          </Box>
        );
      },
    },
    {
      key: 'roles',
      label: 'Roles',
      maxWidth: '100px',
      render: (value, record) => <RoleIconField record={record} />,
    },
    {
      key: 'actions',
      label: 'Acciones',
      width: '120px',
      align: 'center',
      render: (value, record) => (
        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          <IconButton
            size="small"
            component={Link}
            to={`/alerts/${record.id}/show`}
            sx={buttonStyle}
            color="primary"
          >
            <ShowIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            component={Link}
            to={`/alerts/${record.id}`}
            sx={buttonStyle}
            color="secondary"
          >
            <EditIcon fontSize="small" color="primary"  />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenDeleteDialog(record)}
            sx={buttonStyle}
            color="error"
            disabled={deleteMutation.isPending}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <>
      <Card elevation={1} sx={{ m: 2, p: 2 }}>
        <Box sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography
            variant='h5'
            component='h1'
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <AlertIcon color='primary' />
            Gestión de Alertas
            {isFetching && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Gestiona las alertas del sistema organizadas por plantas y zonas
          </Typography>
        </Box>

        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='h6' gutterBottom color='primary'>
            Filtros de Búsqueda
          </Typography>

          <HierarchicalFilter
            levels={['plant', 'zone']}
            onChange={handleFilterChange}
            showTable={false}
          />

          <Box sx={{ mt: 2 }}>
            <DebouncedSearchInput 
              onSearchChange={handleSearchChange}
              initialValue={searchQuery}
            />
          </Box>

          {(filterValues.plantId || filterValues.zoneId) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                Selección actual:
                {filterValues.plantName && ` Planta: ${filterValues.plantName}`}
                {filterValues.zoneName && ` → Zona: ${filterValues.zoneName}`}
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant='h6'>Alertas ({totalCount})</Typography>
            <AlertListActions />
          </Box>

          {alertsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error cargando alertas: {alertsError.message}
            </Alert>
          )}

          {isLoadingAlerts && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {!isLoadingAlerts && alerts.length === 0 && (
            <Alert severity="info">No se encontraron alertas con los filtros actuales</Alert>
          )}

          {alerts.length > 0 && (
            <>
              {showPagination && (
                <PaginationControls
                  totalCount={totalCount}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  pageSizeOptions={pageSizeOptions}
                  showItemsPerPage={true}
                />
              )}
              
              <TableComponent
                data={alerts}
                columns={alertColumns}
                loading={isLoadingAlerts}
                emptyMessage="No se encontraron alertas"
              />

              {showPagination && (
                <PaginationControls
                  totalCount={totalCount}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  pageSizeOptions={pageSizeOptions}
                  showItemsPerPage={false}
                />
              )}
            </>
          )}
        </Box>
      </Card>

      {/* Diálogo de confirmación de eliminación */}
      <DeleteWithConfirmationDialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteConfirm}
        record={deleteDialog.record}
        resource="alerts"
        nameField="name"
        displayName={deleteDialog.record?.name}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
};

export default AlertsList;